import { Injectable, NgZone, inject } from "@angular/core";
import { BaseNotifier } from "../../../../shared/notifiers/base-notifier";
import { Ref } from "../../../../shared/providers/ref";
import { MapProvider } from "../map/map-provider";
import { OverviewMapState } from "./overview-map-state";
import ArcGISMap from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Polygon from "@arcgis/core/geometry/Polygon";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";

@Injectable()
export class OverviewMapNotifier extends BaseNotifier<OverviewMapState> {
  private static readonly INITIAL: OverviewMapState = {
    initialized: false,
    visible: true,
    loading: true,
  };

  private overviewView: MapView | null = null;
  private extentGraphic: Graphic | null = null;
  private container: HTMLDivElement | null = null;
  /** Identidad de vista principal actualmente suscrita. */
  private _boundMainView: MapView | null = null;
  /** Handles sobre la vista principal (se liberan al re-sincronizar). */
  private _mainHandles: Array<{ remove(): void }> = [];
  private readonly zone = inject(NgZone);

  constructor(ref: Ref) {
    super(ref, OverviewMapNotifier.INITIAL);
  }

  get view(): MapView | null {
    return this.overviewView;
  }

  setLoading(loading: boolean): void {
    this.updateState((s) => ({ ...s, loading }));
  }

  init(containerEl: HTMLDivElement): void {
    this.destroy();
    this.container = containerEl;

    // Reactivo: la identidad de la vista principal (aparición, cambio,
    // ausencia) gobierna el ciclo de vida de la overview. Sin polling.
    this.listen(() => {
      const state = this.ref.watch(MapProvider).state();
      const view =
        state.view && !state.view.destroyed ? (state.view as MapView) : null;
      this.syncWithMainView(view, state.map);
    });
  }

  private syncWithMainView(view: MapView | null, map: ArcGISMap | null): void {
    if (!this.container) return;

    // Misma instancia viva ya suscrita: nada que hacer.
    if (view && map && view === this._boundMainView && !view.destroyed) return;

    // Vista ausente/destruida o cambio de identidad → desmontar.
    if (!view || !map || view.destroyed) {
      this.teardownOverview();
      return;
    }

    this.teardownOverview();
    this.zone.runOutsideAngular(() => void this.createOverview(view, map));
    this._boundMainView = view;
  }

  private teardownOverview(): void {
    for (const h of this._mainHandles.splice(0)) {
      try {
        h.remove();
      } catch {}
    }
    if (this.overviewView) {
      try {
        this.overviewView.destroy();
      } catch {}
      this.overviewView = null;
    }
    this.extentGraphic = null;
    this.updateState((s) => ({ ...s, initialized: false, loading: true }));
  }

  private async createOverview(
    mainView: MapView,
    mainMap: ArcGISMap,
  ): Promise<void> {
    if (!this.container) return;

    const overviewMap = new ArcGISMap({ basemap: mainMap.basemap });
    const extentLayer = new GraphicsLayer({
      title: "overview-extent",
      listMode: "hide",
    });
    overviewMap.add(extentLayer);

    this.overviewView = new MapView({
      container: this.container,
      map: overviewMap,
      scale: mainView.scale * 6,
      constraints: { rotationEnabled: false },
      ui: { components: [] } as any,
      popup: {
        dockEnabled: false,
        defaultPopupTemplateEnabled: false,
        autoCloseEnabled: true,
        collapseEnabled: false,
      } as any,
      navigation: {
        browserTouchPanEnabled: false,
        momentumEnabled: false,
      } as any,
    });
    await this.overviewView.when();
    // Igual que en el mapa principal: quitar la atribución de Esri ("Powered by Esri").
    try {
      this.overviewView.ui.remove("attribution");
    } catch {}

    this.extentGraphic = new Graphic({
      symbol: new SimpleFillSymbol({
        color: [59, 130, 246, 0.12],
        outline: new SimpleLineSymbol({
          color: [59, 130, 246, 1],
          width: 1.5,
          style: "dash" as any,
        }),
      }),
    });
    extentLayer.add(this.extentGraphic);

    const updateExtent = () => this.syncExtent(mainView);
    // Watches sobre la vista principal: liberados en cada re-sincronía.
    this._mainHandles.push(
      reactiveUtils.watch(
        () => mainView.extent,
        () => updateExtent(),
      ),
      reactiveUtils.watch(
        () => mainView.stationary,
        () => {
          if (mainView.stationary) updateExtent();
        },
      ),
    );
    updateExtent();

    this.updateState((s) => ({ ...s, initialized: true }));
  }

  toggle(): void {
    const next = !this.snapshot().visible;
    this.updateState((s) => ({ ...s, visible: next }));
    if (next && this.overviewView) {
      setTimeout(
        () =>
          this.overviewView!.goTo({ scale: this.overviewView!.scale }).catch(
            () => {},
          ),
        150,
      );
    }
  }

  destroy(): void {
    for (const h of this._mainHandles.splice(0)) {
      try {
        h.remove();
      } catch {}
    }
    this._boundMainView = null;
    if (this.overviewView) {
      try {
        this.overviewView.destroy();
      } catch {}
      this.overviewView = null;
    }
    this.extentGraphic = null;
    this.container = null;
    this.updateState((s) => ({ ...s, initialized: false, loading: true }));
  }

  /**
   * Dispose automático al salir del viewer / cerrar sesión.
   */
  protected override onDispose(): void {
    this.destroy();
  }

  private syncExtent(mainView: MapView): void {
    if (!this.overviewView || !this.extentGraphic) return;
    const ext = mainView.extent;
    const center = mainView.center;
    if (!ext || !center) return;
    this.overviewView
      .goTo({ center: center.clone(), scale: mainView.scale * 6 })
      .catch(() => {});

    const polygon = Polygon.fromExtent(ext);
    const sr = mainView.spatialReference;
    if (!sr) return;
    polygon.spatialReference = sr;
    this.extentGraphic.geometry = polygon;
  }
}
